import { fetchAuthSession } from "aws-amplify/auth";
import { type ZodType } from "zod";

import { parseGroupsClaim } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import { getLocale } from "@recobra/i18n/runtime";
import { visibleSchema } from "@recobra/schemas/utils";

type AuthedFetchResult = {
  res: Response;
  cognitoGroups: string[];
};

export class HttpError extends Error {
  readonly status: number;
  readonly body: string;
  readonly data: unknown;

  constructor(status: number, body: string, data: unknown) {
    super(`request failed (${status}): ${body}`);
    this.name = "HttpError";
    this.status = status;
    this.body = body;
    this.data = data;
  }
}

const buildFetch = async (path: string, init: RequestInit = {}): Promise<AuthedFetchResult> => {
  const auth = await getAuthContext();
  const res = await fetch(`${import.meta.env.VITE_API_URL}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      "accept-language": getLocale(),
      ...auth.headers,
      ...init.headers,
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new HttpError(res.status, body, parseJson(body));
  }

  return { res, cognitoGroups: auth.cognitoGroups };
};

export const requestJson = async <T>(
  path: string,
  schema: ZodType<T>,
  init: RequestInit = {},
): Promise<T> => {
  const { res, cognitoGroups } = await buildFetch(path, init);
  if (res.status === 204) return null as unknown as T;
  const data: unknown = await res.json();
  const callerVisibleSchema = visibleSchema(schema, cognitoGroups) as ZodType<T>;

  const result = callerVisibleSchema.safeParse(data);
  if (!result.success) {
    console.error(`[schema] parse error for ${init.method ?? "GET"} ${path}:`, result.error.issues);

    throw result.error;
  }

  return result.data;
};

/** Like {@link requestJson} but explicitly typed to return `null` on a 204 No Content convergence response. */
export const requestJsonOrNoContent = async <T>(
  path: string,
  schema: ZodType<T>,
  init: RequestInit = {},
): Promise<T | null> => requestJson(path, schema, init);

export const requestVoid = async (path: string, init: RequestInit = {}): Promise<void> => {
  await buildFetch(path, init);
};

const getAuthContext = async (): Promise<{
  headers: Record<string, string>;
  cognitoGroups: string[];
}> => {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken;
  if (!idToken) throw new Error("not authenticated");
  const token = idToken.toString();

  return {
    headers: { Authorization: `Bearer ${token}` },
    cognitoGroups: parseGroupsClaim(idToken.payload["cognito:groups"]),
  };
};

const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

/**
 * Only ever returns a message safe to show a user: the backend's own
 * `{ error: string }` body when present, otherwise a generic fallback.
 * Never surfaces a raw exception message — a client-side Zod parse failure,
 * an HTTP error without that shape, or anything else — since those can
 * contain full validation-issue dumps or stack-trace-like internals.
 */
export const getErrorMessage = (error: unknown): string => {
  if (
    error instanceof HttpError &&
    error.data &&
    typeof error.data === "object" &&
    "error" in error.data &&
    typeof error.data.error === "string"
  ) {
    return error.data.error;
  }

  return m.common_unexpected_error();
};
